import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/utils/dateLocale';
import { StyledSelectionList } from '@/components/StyledSelectionList';
import {
  buildStageMatchdays,
  getStageMatchdayKey,
  buildExtraRound,
  detectInitialMatchday,
  detectActiveMatch,
  type StageMatchday,
  type MatchForMatchday,
} from '@/utils/matchdayHelpers';
import styles from './styles.module.scss';

/** Number of rounds to load on either side of the active round, both initially
 * and on each lazy-load expansion at the top / bottom edge. */
const WINDOW_RADIUS = 5;

interface LoadedRange {
  start: number;
  end: number;
}

export interface RoundedMatchesScrollerProps<M extends MatchForMatchday> {
  /**
   * Full match slate of the tournament (the tournament Matches tab is the only caller).
   * Grouping into rounds, windowing, and lazy load are derived from this
   * array — the caller does no slicing of its own. Any synthetic/placeholder
   * rows (e.g. knockout-bracket templates) must already be injected by the
   * caller before the array is handed over.
   */
  matches: M[];
  /**
   * Renders a single match row. Link target, user predictions and
   * coefficients live inside this callback — the scroller owns only the
   * round grouping, windowing and scroll mechanics.
   */
  renderCard: (match: M) => React.ReactNode;
}

/**
 * Windowed continuous-scroll list of match rounds.
 *
 * Owns: round/date grouping, the ±WINDOW_RADIUS render window, the
 * IntersectionObserver edge lazy load (throbber + scroll lock + two-pass
 * anchor restoration), the debounced scroll-driven active-round tracking,
 * the pill rail sync, and the initial round / active match auto-scroll.
 */
export function RoundedMatchesScroller<M extends MatchForMatchday>({
  matches,
  renderCard,
}: RoundedMatchesScrollerProps<M>): React.ReactElement | null {
  const { t } = useTranslation(['tournament', 'common']);

  const [selectedStageMatchday, setSelectedStageMatchday] = useState<string | null>(null);
  const [loadedRange, setLoadedRange] = useState<LoadedRange | null>(null);
  // Lazy-load UX state — when the top / bottom IntersectionObserver sentinel
  // fires, we show an in-flow throbber for ~300ms before actually expanding
  // `loadedRange`. The data is already in React Query cache, so the delay is
  // purely a perceived-performance hint that "something is happening" (in the
  // Doherty productive-interaction window). The throbber sits between the
  // sentinel and the round list so it visually anchors where the new content
  // will appear.
  const [pendingPrepend, setPendingPrepend] = useState(false);
  const [pendingAppend, setPendingAppend] = useState(false);
  // Incrementing counter that the scroll-to-pending-target layout effect
  // depends on. Bumped on every pill click / initial-detection scroll request
  // so the effect fires even when `loadedRange` (and therefore
  // `visibleRounds`) doesn't change — i.e. when the target round is already
  // inside the loaded window. Without this, clicking a pill for an already-
  // loaded round wouldn't trigger a scroll, and the IntersectionObserver
  // would later snap the active pill back to the round still pinned at top.
  const [scrollRequestSeq, setScrollRequestSeq] = useState(0);

  // Refs.
  // `scrollContainerRef` is the bounded-height scroll surface for the round list.
  // `roundSectionRefs` lets pill clicks and the IntersectionObserver address
  // section DOM nodes by round id.
  // `selectedRoundRef` mirrors `selectedStageMatchday` for use inside the
  // IntersectionObserver callback without re-creating the observer on every
  // state change.
  // `pendingScrollTargetRef` carries a round id from a pill click that needs
  // to scroll into view after the next render (e.g. when the click expanded
  // the window).
  // `suppressObserverUntilRef` is a small time window during which scroll-
  // driven active-round updates from the IO callback are ignored — set when
  // a pill click is in flight so the smooth scroll doesn't race with the
  // observer and re-trigger setSelectedStageMatchday with intermediate values.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const roundSectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const selectedRoundRef = useRef<string | null>(null);
  const pendingScrollTargetRef = useRef<string | null>(null);
  const pendingScrollBehaviorRef = useRef<ScrollBehavior>('smooth');
  const suppressObserverUntilRef = useRef<number>(0);
  // Tracks whether the scroll-driven active-round evaluation has already run
  // once. Stays true after the first invocation so subsequent re-mounts of
  // the scroll-listener useEffect (triggered by lazy-load expanding
  // `visibleRounds`) don't synchronously re-evaluate and yank the pill to a
  // new round mid-scroll. The user is already scrolling when lazy load fires;
  // their next scroll event (or the next debounce flush from in-flight scroll
  // events) is what should drive the next pill update.
  const hasEvaluatedActiveRoundRef = useRef(false);
  // Timer refs for the throbber-then-load delay. We keep them outside the
  // observer callback so the cleanup `useEffect` (and rapid sentinel flicks)
  // can cancel a pending expansion without stacking timers.
  const prependTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // One-shot guard for the auto-scroll-to-active-match layout effect.
  // Stays true after the first successful scroll so that lazy-load
  // expansions (visibleRounds changing) don't re-yank the user back to the
  // active match after they've started scrolling around.
  const hasAutoScrolledToMatchRef = useRef(false);
  // Snapshot of scrollTop at the moment we first observe the scroll
  // container — lets us detect "user already scrolled" and bail out of the
  // auto-scroll override (Math.abs(current - initial) > 4 means it's
  // intentional movement, not a sub-pixel layout twitch).
  const initialScrollTopRef = useRef<number | null>(null);

  const stageMatchdays = useMemo(() => buildStageMatchdays(matches), [matches]);

  const { stageMatchdaysWithExtra, extraMatches, extraMatchGroups } = useMemo(
    () => buildExtraRound(stageMatchdays, matches),
    [stageMatchdays, matches],
  );

  // Smart auto-detection of current stage-matchday + initial window setup.
  // Runs once when matchdays arrive and `loadedRange` is still null. Resolves
  // an active round id from `detectInitialMatchday`, then centres a ±5-round
  // window around its index in `stageMatchdaysWithExtra`.
  useEffect(() => {
    if (stageMatchdaysWithExtra.length === 0 || loadedRange !== null) return;
    const detected = detectInitialMatchday(stageMatchdaysWithExtra, extraMatches);
    if (!detected) return;
    const idx = stageMatchdaysWithExtra.findIndex(r => r.id === detected);
    if (idx === -1) return;
    const start = Math.max(0, idx - WINDOW_RADIUS);
    const end = Math.min(stageMatchdaysWithExtra.length - 1, idx + WINDOW_RADIUS);
    setLoadedRange({ start, end });
    setSelectedStageMatchday(detected);
    selectedRoundRef.current = detected;
    // Mark this round for an instant scroll-into-view in the next layout
    // effect so the page mounts on the detected round (not on the first
    // round in the loaded window). Without this, when current round is 20
    // and the window is 18-22, the user would land on round 18 at the top.
    pendingScrollTargetRef.current = detected;
    pendingScrollBehaviorRef.current = 'auto';
    suppressObserverUntilRef.current = Date.now() + 150;
    setScrollRequestSeq(s => s + 1);
  }, [stageMatchdaysWithExtra, extraMatches, loadedRange]);

  // Keep selectedRoundRef in sync for use inside observer callbacks.
  useEffect(() => {
    selectedRoundRef.current = selectedStageMatchday;
  }, [selectedStageMatchday]);

  // Visible rounds — slice of the master list.
  const visibleRounds = useMemo(() => {
    if (!loadedRange) return [];
    return stageMatchdaysWithExtra.slice(loadedRange.start, loadedRange.end + 1);
  }, [stageMatchdaysWithExtra, loadedRange]);

  // Pill row options. Always the full list — pills don't depend on the
  // currently loaded window.
  const selectionListOptions = useMemo(
    () => stageMatchdaysWithExtra.map(sm => ({ label: sm.label, value: sm.id })),
    [stageMatchdaysWithExtra],
  );

  // Resolve matches for a specific round. Extra rounds use the precomputed
  // grouping; regular rounds match by composite `stage`/`matchday` key.
  const getMatchesForRound = useCallback(
    (round: StageMatchday): M[] => {
      if (round.id.startsWith('EXTRA_')) {
        return extraMatchGroups[round.id] || [];
      }
      return matches.filter((m: M) => getStageMatchdayKey(m) === round.id);
    },
    [matches, extraMatchGroups],
  );

  // Active-match id within the currently selected round. Scoped to the
  // active round only — global selection would highlight (and try to scroll
  // to) e.g. a Round-of-32 placeholder while the user is looking at Group
  // Stage 2. `detectActiveMatch` returns the live match (or, if none is
  // live, the soonest upcoming match) or null when the round is finished.
  const activeMatchId = useMemo<number | null>(() => {
    if (!selectedStageMatchday) return null;
    const round = stageMatchdaysWithExtra.find(r => r.id === selectedStageMatchday);
    if (!round) return null;
    const matchesInRound = getMatchesForRound(round);
    const active = detectActiveMatch(matchesInRound);
    return active ? active.id : null;
  }, [selectedStageMatchday, stageMatchdaysWithExtra, getMatchesForRound]);

  // Pill click handler.
  // Three concerns:
  //   1. Expand `loadedRange` to include the clicked round if it's outside
  //      the currently loaded window. Use a centred ±5 window around the
  //      target so smooth-scroll has rounds above and below to land on.
  //   2. Defer the actual scroll-into-view to a useLayoutEffect that fires
  //      after the DOM has the target section attached. Set
  //      `suppressObserverUntilRef` so the smooth scroll doesn't race with
  //      the IO callback (which would re-set selectedStageMatchday with
  //      intermediate round ids during the smooth animation).
  //   3. For far jumps (> 2 sections) OR when prefers-reduced-motion is set,
  //      use instant scroll (behavior: 'auto'). Smooth-scrolling across many
  //      sections takes 1-2s during which intermediate round headers cross
  //      the top edge and would re-trigger the observer despite suppression.
  const handleMatchdaySelect = useCallback(
    (value: string | number) => {
      const targetId = String(value);
      const targetIdx = stageMatchdaysWithExtra.findIndex(r => r.id === targetId);
      if (targetIdx === -1) return;

      const currentIdx = selectedRoundRef.current
        ? stageMatchdaysWithExtra.findIndex(r => r.id === selectedRoundRef.current)
        : -1;
      const distance = currentIdx === -1 ? Infinity : Math.abs(targetIdx - currentIdx);
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isInstant = prefersReducedMotion || distance > 2;

      setSelectedStageMatchday(targetId);
      selectedRoundRef.current = targetId;
      pendingScrollTargetRef.current = targetId;
      pendingScrollBehaviorRef.current = isInstant ? 'auto' : 'smooth';
      // Suppress IO-driven active updates. Instant scroll lands in one frame
      // so a short suppression window suffices; smooth scroll covers the
      // ~500ms browser animation plus padding.
      suppressObserverUntilRef.current = Date.now() + (isInstant ? 150 : 700);
      // Bump the request sequence so the scroll layout effect runs even when
      // the target round is already inside the loaded window and
      // `visibleRounds` doesn't change.
      setScrollRequestSeq(s => s + 1);

      // Ensure the clicked round is inside the loaded window. If not, reset
      // the window to a ±5 range centred on the click target.
      setLoadedRange(prev => {
        if (prev && targetIdx >= prev.start && targetIdx <= prev.end) return prev;
        const start = Math.max(0, targetIdx - WINDOW_RADIUS);
        const end = Math.min(stageMatchdaysWithExtra.length - 1, targetIdx + WINDOW_RADIUS);
        return { start, end };
      });
    },
    [stageMatchdaysWithExtra],
  );

  // After the render that satisfies the pending scroll target, smooth-scroll
  // the corresponding section into view. useLayoutEffect (not useEffect) so
  // it runs before paint after the section node is attached, avoiding a
  // visible flash of the wrong section at the top of the scroll container.
  useLayoutEffect(() => {
    const targetId = pendingScrollTargetRef.current;
    if (!targetId) return;
    const node = roundSectionRefs.current.get(targetId);
    const container = scrollContainerRef.current;
    if (!node || !container) return;
    // Scroll only the inner container — `node.scrollIntoView()` walks up the
    // ancestor chain and scrolls every scrollable ancestor (including the
    // document) which would push the page header / pill row out of view.
    // Use bounding rects so the math is independent of offsetParent layout.
    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const offset = container.scrollTop + (nodeRect.top - containerRect.top);
    container.scrollTo({ top: offset, behavior: pendingScrollBehaviorRef.current });
    pendingScrollTargetRef.current = null;
    // Both deps matter:
    //   - `scrollRequestSeq` guarantees the effect fires on every pill click,
    //     including when the target round is already inside the loaded
    //     window (so `visibleRounds` reference doesn't change).
    //   - `visibleRounds` guarantees the effect fires after a window
    //     expansion mounts new round sections, so the target node exists in
    //     the refs map when we look it up.
  }, [scrollRequestSeq, visibleRounds]);

  // Auto-scroll to the active match within the selected round on mount.
  // SIBLING to (runs AFTER) the round-section scroll above so that when no
  // active match exists, the baseline round-top scroll still wins; when an
  // active match exists, this overrides scrollTop to land on the match card
  // with a small breathing room under the round header.
  //
  // Guards:
  //   1. Skip while a lazy-load expansion is in flight — `pending*` owns
  //      scroll position via the anchor restoration pipeline.
  //   2. One-shot per mount via `hasAutoScrolledToMatchRef` — once we've
  //      landed on the active match, the user owns scroll position.
  //   3. Bail if the user has already manually scrolled away from the
  //      initial position by more than 4px (sub-pixel layout twitch
  //      tolerance) — respects intentional scroll input that races our
  //      auto-scroll.
  //   4. Silently no-op if the active match's DOM node isn't mounted yet
  //      (the round may not be in `visibleRounds`). The effect re-runs on
  //      `visibleRounds` change and will succeed once the node attaches.
  useLayoutEffect(() => {
    if (pendingPrepend || pendingAppend) return;
    if (hasAutoScrolledToMatchRef.current) return;
    if (activeMatchId == null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (
      initialScrollTopRef.current !== null &&
      Math.abs(container.scrollTop - initialScrollTopRef.current) > 4
    ) {
      return;
    }
    const node = container.querySelector<HTMLElement>(`[data-match-id="${activeMatchId}"]`);
    if (!node) return; // round not mounted yet — wait for next visibleRounds tick
    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    // 16px breathing room under the round header so the active card isn't
    // flush against the header band.
    const delta = (nodeRect.top - containerRect.top) - 16;
    container.scrollTop += delta;
    hasAutoScrolledToMatchRef.current = true;
    // Refresh the initial scrollTop snapshot to the post-auto-scroll value
    // so subsequent user scrolls are measured from "where we landed", not
    // from the pre-auto-scroll baseline.
    initialScrollTopRef.current = container.scrollTop;
  }, [scrollRequestSeq, visibleRounds, activeMatchId, pendingPrepend, pendingAppend]);

  // Scroll-driven active-round tracking.
  // For each scroll event (debounced), find the round section that
  // currently covers the top of the scroll container and treat it as active.
  // Previously this used IntersectionObserver with `top <= 16` against
  // viewport coordinates, but the scroll container sits ~200px down the page
  // so viewport-top <= 16 was never true for sections at the container's
  // top — observer effectively never updated the active round on scroll.
  // A scroll listener + bounding-rect math relative to the container is
  // simpler and works in both directions (up and down).
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    if (visibleRounds.length === 0) return;

    // Capture the initial scrollTop on the very first mount so the
    // auto-scroll-to-active-match layout effect can detect "user already
    // scrolled" before its first chance to fire. Only set once — subsequent
    // re-runs (visibleRounds expanding) must not overwrite the baseline.
    if (initialScrollTopRef.current === null) {
      initialScrollTopRef.current = root.scrollTop;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const evaluate = () => {
      timeoutId = null;
      if (Date.now() < suppressObserverUntilRef.current) return;
      const containerRect = root.getBoundingClientRect();
      const containerTop = containerRect.top;
      // Pick the section whose top is at or just above the container top
      // and whose bottom is still below it — i.e. the section currently
      // covering the top boundary. Among candidates, the one with the
      // largest relativeTop (closest to 0 from below) wins.
      let bestId: string | null = null;
      let bestRelTop = Number.NEGATIVE_INFINITY;
      roundSectionRefs.current.forEach((node, id) => {
        const rect = node.getBoundingClientRect();
        const relTop = rect.top - containerTop;
        const relBottom = rect.bottom - containerTop;
        if (relTop <= 16 && relBottom > 0 && relTop > bestRelTop) {
          bestRelTop = relTop;
          bestId = id;
        }
      });
      if (bestId && bestId !== selectedRoundRef.current) {
        const nextId: string = bestId;
        selectedRoundRef.current = nextId;
        setSelectedStageMatchday(nextId);
      }
    };

    // 80ms debounce — collapses rapid scroll events (notably iOS momentum
    // scroll in Telegram WebView, which can deliver 60+ scroll events per
    // second during a flick) into a single state update. Without this,
    // crossing 3 round sections in 100ms produced 3 rapid pill swaps that
    // read as "blinking". 80ms is short enough to feel responsive during
    // slow drag and long enough to coalesce a fast flick into one update.
    const onScroll = () => {
      if (timeoutId !== null) return;
      timeoutId = setTimeout(evaluate, 80);
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    // Initial evaluation so the active round is correct on mount (e.g. after
    // the initial scroll-into-view lands on the detected round). Only on the
    // very first mount — on subsequent re-runs (visibleRounds expanded by
    // lazy-load), the user is actively scrolling and a synchronous re-eval
    // would yank the pill to a new round mid-scroll, bypassing the 80ms
    // debounce and producing the "blink at lazy-load boundary" the user sees.
    // The in-flight scroll events will trigger the next debounced evaluation
    // naturally.
    if (!hasEvaluatedActiveRoundRef.current) {
      hasEvaluatedActiveRoundRef.current = true;
      evaluate();
    }

    return () => {
      root.removeEventListener('scroll', onScroll);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [visibleRounds]);

  // Lazy-load on edge — the only path that grows the loaded window. Rounds
  // the user never scrolls to are never mounted, which keeps the DOM (and
  // its teardown cost when the user navigates away) small.
  //
  // Prepend 5 more rounds when the top sentinel enters the viewport, append
  // 5 more when the bottom sentinel enters. Scroll position across a prepend
  // is preserved with an *anchor* — at prepend
  // time we record the round currently sitting at the top of the viewport
  // plus its distance from the container's top edge, then after re-render
  // we restore `scrollTop = anchorNode.offsetTop - savedOffsetFromTop`.
  // `offsetTop` reflects the section's POST-COMMIT layout position so any
  // late layout shift between commit and the layout effect (web-font swap
  // on round/date headers, async style recompute on newly mounted
  // styled-components, late image decode) cannot desync the anchor — which
  // a `scrollHeight - prevScrollHeight` delta could (and visibly did) miss
  // by tens of pixels, surfacing as the "jumps to R21 when scrolling up
  // through R23" symptom.
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  // Anchor captured immediately before a prepend, consumed in the next
  // useLayoutEffect to restore scrollTop relative to the anchor section's
  // new offsetTop.
  const prependAnchorRef = useRef<{ id: string; offsetFromTop: number } | null>(null);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const topNode = topSentinelRef.current;
    const bottomNode = bottomSentinelRef.current;
    if (!root || !topNode || !bottomNode || !loadedRange) return;

    const observer = new IntersectionObserver(
      entries => {
        // Respect the global suppression window. This breaks the cascade
        // chain: after a prepend lands and the anchor restoration runs, the
        // top sentinel may still be intersecting (if the restoration's
        // synchronous pass + rAF pass haven't fully landed scrollTop yet,
        // or on iOS WebView where momentum scroll keeps scrollTop near 0
        // for several frames after the synchronous write). Without this
        // guard, the IO would fire AGAIN immediately, prepending another
        // batch before the restoration settles, all the way to start=0 —
        // the "jumps to the topmost loaded round" user-reported symptom.
        // The window is primed to `now + 250 + 300` by the IO callback
        // itself when it schedules a prepend/append, so cascades are
        // naturally short-circuited until restoration has completed.
        if (Date.now() < suppressObserverUntilRef.current) return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target === topNode) {
            // Prepend: only if there are still earlier rounds to load.
            if (loadedRange.start > 0) {
              // Debounce — if a prepend timer is already scheduled, ignore
              // additional intersect events from rapid sentinel flicks.
              // Without this, a fast flick into the rootMargin band would
              // re-fire intersect multiple times before the 300ms timer
              // flushes, stacking multiple `setLoadedRange` calls and
              // double-expanding the window. We gate on the ref (not the
              // `pendingPrepend` state) because the ref is the authoritative
              // up-to-date signal inside the IO closure — state would be
              // stale if the effect deps haven't refreshed the closure yet.
              if (prependTimerRef.current !== null) continue;
              // Anchor capture is deferred to the `[pendingPrepend]` layout
              // effect below. At this moment R-top is still at viewport y≈0,
              // but the throbber will push it down by ~48px on the next
              // commit. Capturing now would save offsetFromTop≈0, then the
              // restoration pass would snap R-top back to y≈0 after the
              // throbber is replaced by loaded rounds, producing a visible
              // upward jump of ~throbber-height pixels. Capturing AFTER the
              // throbber renders saves offsetFromTop≈48 so R-top stays at
              // y≈48 across the entire throbber→rounds transition.
              // Mute scroll-driven active-round evaluation for the full
              // throbber-visible window + post-prepend reflow / image
              // decode budget. 250 + 300 covers the 300ms delay plus the
              // existing 250ms post-commit settle period that protects
              // against match-card team-logo decode shifting the
              // top-of-container band by ≤16px and producing pill blinks
              // around the active round.
              suppressObserverUntilRef.current = Date.now() + 250 + 300;
              // Show throbber in-flow; the layout effect keyed on
              // `pendingPrepend` will compensate scroll position for the
              // throbber's height so the anchor stays pinned.
              setPendingPrepend(true);
              // Schedule the actual range expansion ~300ms later. 300ms
              // sits inside the Doherty threshold for productive
              // interaction — long enough to register as intentional UI
              // feedback, short enough not to feel laggy.
              prependTimerRef.current = setTimeout(() => {
                prependTimerRef.current = null;
                setLoadedRange(prev => {
                  if (!prev) return prev;
                  if (prev.start === 0) return prev;
                  return {
                    start: Math.max(0, prev.start - WINDOW_RADIUS),
                    end: prev.end,
                  };
                });
                setPendingPrepend(false);
              }, 300);
            }
          } else if (entry.target === bottomNode) {
            if (loadedRange.end < stageMatchdaysWithExtra.length - 1) {
              // Debounce — same rationale as prepend: a rapid flick into
              // the bottom rootMargin band can fire intersect multiple
              // times before the 300ms timer flushes. Gate on the ref so
              // the closure stays correct without taking `pendingAppend`
              // as an effect dep (which would self-trigger the cleanup).
              if (appendTimerRef.current !== null) continue;
              // Same defensive guard on append. Append does not shift the
              // user's visible position (new sections mount below the
              // viewport), so no anchor capture is needed — but the
              // throbber-appearing pass adds height at the bottom of the
              // scroll content which is harmless to the top-anchored view.
              suppressObserverUntilRef.current = Date.now() + 250 + 300;
              setPendingAppend(true);
              appendTimerRef.current = setTimeout(() => {
                appendTimerRef.current = null;
                setLoadedRange(prev => {
                  if (!prev) return prev;
                  if (prev.end === stageMatchdaysWithExtra.length - 1) return prev;
                  return {
                    start: prev.start,
                    end: Math.min(stageMatchdaysWithExtra.length - 1, prev.end + WINDOW_RADIUS),
                  };
                });
                setPendingAppend(false);
              }, 300);
            }
          }
        }
      },
      {
        root,
        // Fire AT the actual edge — no lookahead. The user must scroll all
        // the way to the topmost (or bottommost) loaded round's header before
        // the throbber pops in. Lookahead (e.g. 400px) made the sentinel
        // intersect while the user was still mid-flick and the momentum-scroll
        // carried the viewport through the throbber's tiny visible window,
        // so the user effectively never saw the throbber appear. With 0
        // rootMargin, the viewport reaches the round header, momentum settles
        // at the boundary (browser default overscroll), and the throbber
        // renders in-flow above the first round — directly where the user is
        // now looking. After the 300ms delay the rounds replace the throbber.
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      },
    );

    observer.observe(topNode);
    observer.observe(bottomNode);

    return () => {
      observer.disconnect();
      // Cancel any pending throbber-then-load timers so a rapid loadedRange
      // update (e.g. via pill click resetting the window) doesn't fight an
      // in-flight expansion queued by a sentinel intersect. If a timer was
      // actually scheduled, we also reset the matching `pending*` state so
      // the throbber doesn't get stranded in the visible-but-no-pending-
      // expansion state. We do NOT unconditionally reset state — only when
      // we cancelled a timer — so the normal happy-path effect re-runs
      // (timer completed, set pending=false itself, range expanded → IO
      // effect re-runs with no timer to cancel) don't clobber state.
      if (prependTimerRef.current !== null) {
        clearTimeout(prependTimerRef.current);
        prependTimerRef.current = null;
        setPendingPrepend(false);
      }
      if (appendTimerRef.current !== null) {
        clearTimeout(appendTimerRef.current);
        appendTimerRef.current = null;
        setPendingAppend(false);
      }
    };
  }, [loadedRange, stageMatchdaysWithExtra.length]);

  // Unmount-time safety net: clear both timers if the component is torn down
  // mid-delay. Without this, a fast tab switch could leave the timer alive
  // and call `setLoadedRange` / `setPendingPrepend` on an unmounted component.
  useEffect(() => {
    return () => {
      if (prependTimerRef.current !== null) {
        clearTimeout(prependTimerRef.current);
        prependTimerRef.current = null;
      }
      if (appendTimerRef.current !== null) {
        clearTimeout(appendTimerRef.current);
        appendTimerRef.current = null;
      }
    };
  }, []);

  // Capture the prepend anchor at the moment the throbber renders — at that
  // point the throbber has already pushed the topmost-loaded round down by
  // ~48px, so `offsetFromTop` reflects that pushed position. When rounds
  // later replace the throbber, anchor restoration uses this offset, keeping
  // the anchored round at the SAME viewport position across the entire
  // throbber→rounds transition. Capturing before the throbber renders gave
  // `offsetFromTop = 1` (round was at viewport top pre-throbber), which then
  // snapped the round 48px UP when rounds replaced the throbber — the
  // user-visible "jump to a different round" symptom.
  useLayoutEffect(() => {
    if (!pendingPrepend) return;
    if (prependAnchorRef.current) return; // already captured — guard against double-fire
    const root = scrollContainerRef.current;
    if (!root) return;
    const containerTop = root.getBoundingClientRect().top;
    // Prefer the round currently tracked as active by the scroll listener
    // (selectedRoundRef) — it's already the "round at the top of the
    // viewport" and is robust against any throbber height variance (spinner
    // size, padding, line-height, dynamic type, etc.). Threshold-based
    // heuristics break when the throbber is taller than the assumed cap.
    let anchorId: string | null = selectedRoundRef.current;
    let anchorOffset = 0;
    if (anchorId) {
      const node = roundSectionRefs.current.get(anchorId);
      if (node) {
        anchorOffset = node.getBoundingClientRect().top - containerTop;
      } else {
        anchorId = null;
      }
    }
    // Fallback: scan for the topmost loaded section whose body intersects
    // the viewport, preferring the one with the LARGEST positive relTop
    // (closest to 0 from below) — i.e. the section the user is currently
    // looking at as their reading anchor. Used only if `selectedRoundRef`
    // is empty (e.g. during the brief window between initial detection and
    // first scroll-listener evaluation).
    if (!anchorId) {
      let bestRelTop = Number.NEGATIVE_INFINITY;
      roundSectionRefs.current.forEach((node, id) => {
        const rect = node.getBoundingClientRect();
        const relTop = rect.top - containerTop;
        const relBottom = rect.bottom - containerTop;
        if (relBottom > 0 && relTop > bestRelTop) {
          bestRelTop = relTop;
          anchorId = id;
          anchorOffset = relTop;
        }
      });
    }
    if (anchorId) {
      prependAnchorRef.current = { id: anchorId, offsetFromTop: anchorOffset };
    }
  }, [pendingPrepend]);

  // After a prepend, restore the user's visible position via the recorded
  // anchor. We use getBoundingClientRect (not offsetTop) for two reasons:
  //   1. BCR forces a synchronous layout flush — robust on iOS WKWebView
  //      where layout may not be fully settled by the time useLayoutEffect
  //      runs after React commits (offsetTop can be stale, producing a wrong
  //      scrollTop that visibly jumps to a different round).
  //   2. Container-relative math (`anchorTop - containerTop`) is independent
  //      of the offsetParent chain, so it stays correct if any wrapper ever
  //      gets `position: relative` inserted between `.matchesScroll` and
  //      `<section>`.
  // We compute the delta between the anchor's CURRENT viewport position and
  // its SAVED position and adjust scrollTop by that delta. Relative adjustment
  // (scrollTop += delta) interrupts iOS momentum scroll more cleanly than an
  // absolute scrollTop assignment, which on iOS WebKit can be ignored or
  // applied with delay if momentum is in-flight at the moment of write.
  // Runs in useLayoutEffect so the adjustment lands before paint.
  // After the synchronous adjustment, a `requestAnimationFrame` safety pass
  // re-measures and re-applies any residual delta — this catches the iOS
  // WebView edge case where momentum scroll continues moving the wrapper
  // position AFTER our useLayoutEffect write, leaving a small but visible
  // jump to the prepended section. Without the rAF pass, on iOS the
  // viewport could end up showing R21 at top instead of staying anchored
  // on R23 (the user-reported "jumps to 21st round inside matchesScroll").
  useLayoutEffect(() => {
    const root = scrollContainerRef.current;
    const anchor = prependAnchorRef.current;
    if (!root || !anchor) return;
    const node = roundSectionRefs.current.get(anchor.id);
    if (!node) {
      prependAnchorRef.current = null;
      return;
    }
    const applyCorrection = () => {
      const containerRect = root.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const currentRelativeTop = nodeRect.top - containerRect.top;
      const delta = currentRelativeTop - anchor.offsetFromTop;
      if (delta !== 0) {
        root.scrollTop += delta;
      }
      return delta;
    };
    // Pass 1: synchronous before paint.
    applyCorrection();
    // Pass 2: post-paint safety net for iOS momentum-scroll interference.
    // No-op if pass 1 already landed perfectly; corrects residual otherwise.
    const rafId = requestAnimationFrame(() => {
      // The anchor map entry might have been swept by a downstream effect;
      // re-fetch defensively.
      const currentNode = roundSectionRefs.current.get(anchor.id);
      if (!currentNode || !scrollContainerRef.current) return;
      applyCorrection();
    });
    // Clear anchor after the visibleRounds change pass landed — the rounds
    // have replaced the throbber and the user's anchored section is back at
    // its captured viewport offset.
    prependAnchorRef.current = null;
    return () => cancelAnimationFrame(rafId);
    // Only `visibleRounds` matters. We do NOT include `pendingPrepend` —
    // when the throbber appears we WANT the content to shift down by the
    // throbber height (~48px) so the user actually SEES the throbber pop in
    // above the first loaded round. Restoring the anchor on `pendingPrepend`
    // toggle would immediately scroll the throbber out of viewport
    // (compensating its height by adjusting scrollTop), making the throbber
    // invisible — exactly the symptom the user reported.
  }, [visibleRounds]);

  // Section ref callback — attach/detach from the refs map by round id.
  const setRoundSectionRef = useCallback(
    (roundId: string) => (node: HTMLElement | null) => {
      if (node) {
        roundSectionRefs.current.set(roundId, node);
      } else {
        roundSectionRefs.current.delete(roundId);
      }
    },
    [],
  );

  // Render the body of a single round — date groups + match cards. No
  // sticky / scroll concerns here; layout is owned by the parent
  // `.matchesScroll` container.
  const renderRoundBody = useCallback(
    (round: StageMatchday): React.ReactNode => {
      const matchesForRound = getMatchesForRound(round);

      if (matchesForRound.length === 0) {
        return (
          <div className={styles.matchesList}>
            <div className={styles.emptyState}>
              <h3 className={styles.emptyStateTitle}>{t('tournament:noMatches')}</h3>
              <p className={styles.emptyStateText}>
                {t('tournament:noMatchesIn', { round: round.label })}
              </p>
            </div>
          </div>
        );
      }

      const matchesByDate = matchesForRound.reduce((acc: Record<string, M[]>, match: M) => {
        const dateKey = format(new Date(match.match_date), 'yyyy-MM-dd');
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(match);
        return acc;
      }, {});

      const isExtraRound = round.id.startsWith('EXTRA_');

      return (
        <div className={styles.matchesList}>
          <div className={styles.matchdayGroup}>
            {Object.keys(matchesByDate)
              .sort()
              .map(dateKey => {
                const dayMatches = matchesByDate[dateKey];
                const matchDate = new Date(dayMatches[0].match_date);

                return (
                  <div key={dateKey} className={styles.dateGroup}>
                    <h4 className={styles.dateHeader}>
                      {format(matchDate, 'd MMMM, EEEE', { locale: getDateLocale() })}
                    </h4>
                    <div className={styles.matchGrid}>
                      {dayMatches.map((match: M) => {
                        const card = renderCard(match);

                        if (isExtraRound && match.matchday) {
                          return (
                            <div key={match.id} className="p-relative">
                              {card}
                              <span className={styles.originalRoundBadge}>
                                {t('tournament:round', { number: match.matchday })}
                              </span>
                            </div>
                          );
                        }

                        return <React.Fragment key={match.id}>{card}</React.Fragment>;
                      })}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      );
    },
    [getMatchesForRound, renderCard, t],
  );

  // Gap between matches arriving and `detectInitialMatchday` selecting the
  // initial round — follows the project's "return null while loading" policy.
  if (stageMatchdaysWithExtra.length > 0 && loadedRange === null) {
    return null;
  }

  if (stageMatchdaysWithExtra.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>{t('tournament:noMatches')}</h3>
        <p className={styles.emptyStateText}>
          {t('tournament:noMatchesInTournament')}
        </p>
      </div>
    );
  }

  return (
    <>
      <StyledSelectionList
        active={selectedStageMatchday || ''}
        setActive={handleMatchdaySelect}
        options={selectionListOptions}
      />
      <div
        ref={scrollContainerRef}
        className={`${styles.matchesScroll} ${pendingPrepend || pendingAppend ? styles.matchesScrollLocked : ''}`}
      >
        <div ref={topSentinelRef} className={styles.sentinel} aria-hidden="true" />
        {pendingPrepend && (
          <div
            className={styles.lazyLoader}
            role="status"
            aria-live="polite"
            aria-label={t('common:loading', 'Loading')}
          >
            <span className="spinner spinner--sm" />
          </div>
        )}
        {visibleRounds.map(round => (
          <section
            key={round.id}
            ref={setRoundSectionRef(round.id)}
            data-round-id={round.id}
            className={styles.roundSection}
          >
            <div className={styles.roundHeader}>
              <h3 className={styles.roundHeaderTitle}>{round.label}</h3>
              <span className={styles.roundHeaderCount}>
                {t('tournament:matchesCount', { count: round.matches_count })}
              </span>
            </div>
            {renderRoundBody(round)}
          </section>
        ))}
        {pendingAppend && (
          <div
            className={styles.lazyLoader}
            role="status"
            aria-live="polite"
            aria-label={t('common:loading', 'Loading')}
          >
            <span className="spinner spinner--sm" />
          </div>
        )}
        <div ref={bottomSentinelRef} className={styles.sentinel} aria-hidden="true" />
      </div>
    </>
  );
}

export default RoundedMatchesScroller;
